import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FolderOpen, Plus, Music, Trash2, Edit2, Check, X, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const CRATE_COLORS = [
  '#3B82F6', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#ec4899', // pink
];

export default function CratesPage() {
  const [crates, setCrates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCrateName, setNewCrateName] = useState('');
  const [newCrateDescription, setNewCrateDescription] = useState('');
  const [newCrateColor, setNewCrateColor] = useState(CRATE_COLORS[0]);
  const [editingCrate, setEditingCrate] = useState(null);

  const fetchCrates = async () => {
    try {
      const response = await axios.get(`${API}/crates`);
      setCrates(response.data.crates || []);
    } catch (e) {
      console.error('Failed to fetch crates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCrates();
  }, []);

  const createCrate = async () => {
    if (!newCrateName.trim()) {
      toast.error('Please enter a crate name');
      return;
    }

    try {
      const response = await axios.post(`${API}/crates`, {
        name: newCrateName,
        description: newCrateDescription,
        color: newCrateColor
      });
      
      if (response.data.success) {
        toast.success('Crate created!');
        setCrates(prev => [...prev, response.data.crate]);
        setShowCreateDialog(false);
        setNewCrateName('');
        setNewCrateDescription('');
        setNewCrateColor(CRATE_COLORS[0]);
      }
    } catch (e) {
      toast.error('Failed to create crate');
    }
  };

  const updateCrate = async (crateId, updates) => {
    try {
      const response = await axios.put(`${API}/crates/${crateId}`, updates);
      if (response.data.success) {
        setCrates(prev => prev.map(c => c.id === crateId ? response.data.crate : c));
        toast.success('Crate updated');
        setEditingCrate(null);
      }
    } catch (e) {
      toast.error('Failed to update crate');
    }
  };

  const deleteCrate = async (crateId) => {
    try {
      await axios.delete(`${API}/crates/${crateId}`);
      toast.success('Crate deleted');
      setCrates(prev => prev.filter(c => c.id !== crateId));
    } catch (e) {
      toast.error('Failed to delete crate');
    }
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 data-page-header="redundant" className="font-heading text-3xl sm:text-4xl font-bold mb-2 text-[#0F172A] tracking-tight">
              Your <span className="gradient-text">crates</span>
            </h1>
            <p className="font-body text-[#64748B] text-sm">
              {crates.length} crate{crates.length !== 1 ? 's' : ''} in your collection
            </p>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary text-xs sm:text-sm px-3 py-2 sm:px-4" data-testid="create-crate-btn">
                <Plus className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">New Crate</span>
                <span className="sm:hidden">New</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#E2E8F0]">
              <DialogHeader>
                <DialogTitle className="font-heading text-[#0F172A]">Create New Crate</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <label className="font-mono text-xs text-[#64748B] uppercase mb-2 block">Name</label>
                  <Input
                    placeholder="Crate name..."
                    value={newCrateName}
                    onChange={(e) => setNewCrateName(e.target.value)}
                    className="bg-white border-[#E2E8F0] text-[#0F172A]"
                    data-testid="crate-name-input"
                  />
                </div>
                <div>
                  <label className="font-mono text-xs text-[#64748B] uppercase mb-2 block">Description (optional)</label>
                  <Input
                    placeholder="Description..."
                    value={newCrateDescription}
                    onChange={(e) => setNewCrateDescription(e.target.value)}
                    className="bg-white border-[#E2E8F0] text-[#0F172A]"
                    data-testid="crate-description-input"
                  />
                </div>
                <div>
                  <label className="font-mono text-xs text-[#64748B] uppercase mb-2 block">Color</label>
                  <div className="flex gap-2 flex-wrap">
                    {CRATE_COLORS.map(color => (
                      <button
                        key={color}
                        onClick={() => setNewCrateColor(color)}
                        className={`w-8 h-8 rounded-full transition-transform ${newCrateColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-black scale-110' : ''}`}
                        style={{ backgroundColor: color }}
                        data-testid={`color-${color}`}
                      />
                    ))}
                  </div>
                </div>
                <Button 
                  onClick={createCrate}
                  className="w-full btn-primary"
                  data-testid="confirm-create-crate"
                >
                  Create Crate
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Crates Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-36 rounded-2xl bg-[#F1F5F9] animate-pulse border border-[#E2E8F0]" />
            ))}
          </div>
        ) : crates.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {crates.map((crate, index) => (
              <motion.div
                key={crate.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <CrateCard 
                  crate={crate} 
                  onUpdate={updateCrate}
                  onDelete={() => deleteCrate(crate.id)}
                  isEditing={editingCrate === crate.id}
                  setEditing={(editing) => setEditingCrate(editing ? crate.id : null)}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 glass-pane">
            <FolderOpen className="w-16 h-16 text-[#1E3A8A] mx-auto mb-4" />
            <p className="font-heading text-xl text-[#64748B] mb-2">No crates yet</p>
            <p className="font-body text-[#94A3B8] text-sm mb-4">
              Create crates to organize your tracks
            </p>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Crate
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function CrateCard({ crate, onUpdate, onDelete, isEditing, setEditing }) {
  const [editName, setEditName] = useState(crate.name);
  const [editDescription, setEditDescription] = useState(crate.description || '');
  const [editColor, setEditColor] = useState(crate.color);

  const handleSave = () => {
    onUpdate(crate.id, {
      name: editName,
      description: editDescription,
      color: editColor
    });
  };

  const handleCancel = () => {
    setEditName(crate.name);
    setEditDescription(crate.description || '');
    setEditColor(crate.color);
    setEditing(false);
  };

  if (isEditing) {
    return (
      <div className="card-tron p-4 space-y-3">
        <Input
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          className="bg-white border-[#E2E8F0] text-[#0F172A]"
          placeholder="Crate name"
        />
        <Input
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          className="bg-white border-[#E2E8F0] text-[#0F172A]"
          placeholder="Description (optional)"
        />
        <div className="flex gap-2 flex-wrap">
          {CRATE_COLORS.map(color => (
            <button
              key={color}
              onClick={() => setEditColor(color)}
              className={`w-6 h-6 rounded-full transition-transform ${editColor === color ? 'ring-2 ring-white scale-110' : ''}`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSave} className="flex-1 bg-green-600 hover:bg-green-500">
            <Check className="w-4 h-4 mr-1" /> Save
          </Button>
          <Button onClick={handleCancel} variant="outline" className="flex-1 border-[#E2E8F0]">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Link to={`/crate/${crate.id}`} data-testid={`crate-card-${crate.id}`}>
      <div className="card-tron h-36 flex flex-col overflow-hidden group relative hover:border-[#2563FF]/50 transition-colors">
        {/* Color bar */}
        <div className="h-1.5" style={{ backgroundColor: crate.color }} />
        
        {/* Menu */}
        <div className="absolute top-3 right-2 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.preventDefault()}>
              <button className="p-1 text-[#64748B] hover:text-[#0F172A] opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white border-[#E2E8F0]">
              <DropdownMenuItem onClick={(e) => { e.preventDefault(); setEditing(true); }} className="text-[#0F172A] hover:bg-[#EFF6FF]">
                <Edit2 className="w-4 h-4 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.preventDefault(); onDelete(); }} className="text-[#2563FF] hover:bg-[#EFF6FF]">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Content */}
        <div className="p-3 flex-1 flex flex-col">
          <div className="flex items-start gap-2 mb-2">
            <div 
              className="w-9 h-9 flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${crate.color}20`, borderColor: crate.color, borderWidth: 1 }}
            >
              <FolderOpen className="w-4 h-4" style={{ color: crate.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-[#0F172A] text-sm font-bold truncate group-hover:text-[#2563FF] transition-colors">
                {crate.name}
              </h3>
              {crate.description && (
                <p className="font-body text-[#64748B] text-xs truncate">
                  {crate.description}
                </p>
              )}
            </div>
          </div>

          <div className="mt-auto flex items-center justify-between">
            <span className="font-mono text-xs text-[#64748B] flex items-center gap-1">
              <Music className="w-3 h-3" />
              {crate.active_track_count ?? crate.track_ids?.length ?? 0} tracks
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
